import {
  CheckCircleFillIcon,
  XCircleFillIcon,
  SkipIcon,
  DotFillIcon,
  StopIcon,
  AlertIcon,
  SquareFillIcon,
  IssueReopenedIcon,
} from '@primer/octicons-react'
import {memo} from 'react'
import type {CheckRunStatus, CheckRunConclusion} from './types'
import styles from './StatusIcon.module.css'

export interface StatusIconProps {
  status: CheckRunStatus
  conclusion: CheckRunConclusion
  size?: number
}

/**
 * Animated spinner SVG for in_progress status.
 * Matches the Ruby StatusComponent implementation.
 */
function AnimatedSpinner({size}: {size: number}) {
  return (
    <div style={{height: size, width: size}}>
      <svg
        aria-label="Currently running"
        width="100%"
        height="100%"
        fill="none"
        viewBox="0 0 16 16"
        className={styles.animRotate}
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill="none"
          stroke="var(--fgColor-attention)"
          strokeWidth="2"
          d="M3.05 3.05a7 7 0 1 1 9.9 9.9 7 7 0 0 1-9.9-9.9Z"
          opacity=".5"
        />
        <path
          fill="var(--fgColor-attention)"
          fillRule="evenodd"
          d="M8 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z"
          clipRule="evenodd"
        />
        <path fill="var(--fgColor-attention)" d="M14 8a6 6 0 0 0-6-6V0a8 8 0 0 1 8 8h-2Z" />
      </svg>
    </div>
  )
}

// eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression intentionally shares the outer name
export const StatusIcon = memo(function StatusIcon({status, conclusion, size = 16}: StatusIconProps) {
  // Completed status - use conclusion to determine icon
  if (status === 'completed') {
    switch (conclusion) {
      case 'success':
        return <CheckCircleFillIcon size={size} className="color-fg-success" />
      case 'failure':
        return <XCircleFillIcon size={size} className="color-fg-danger" />
      case 'cancelled':
        return <StopIcon size={size} className={styles.neutralCheck} />
      case 'skipped':
        return <SkipIcon size={size} className={styles.neutralCheck} />
      case 'neutral':
        return <SquareFillIcon size={size} className={styles.neutralCheck} />
      case 'timed_out':
        return <XCircleFillIcon size={size} className="color-fg-danger" />
      case 'action_required':
        return <AlertIcon size={size} className="color-fg-attention" />
      default:
        return <IssueReopenedIcon size={size} className={styles.neutralCheck} />
    }
  }

  // In progress - show animated spinner
  if (status === 'in_progress') {
    return <AnimatedSpinner size={size} />
  }

  // Queued - yellow/pending dot
  if (status === 'queued') {
    return <DotFillIcon size={size} className={styles.pendingIcon} />
  }

  // Fallback for any other status
  return <IssueReopenedIcon size={size} className={styles.neutralCheck} />
})
